/**
 * useNavBots — the bot list for the sidebar nav ("Your bots"), scoped to the
 * bot's OWNING customer rather than the logged-in caller.
 *
 * The bug this fixes: on a bot page, the chrome fetched `/api/customer/bots`
 * (which resolves to the *caller's* bots). When an admin/reseller impersonates
 * a customer (viewing one of that customer's bots), the nav then showed the
 * operator's own bots instead of the customer's. The owner is read from the
 * bot's config (`GET /api/bot/{id}/config` → `customer_id`), and the bot list is
 * fetched scoped to it via `/api/customer/bots?customer_id=<owner>` — which the
 * backend honors for admin/reseller (ResolveCustomerIDForJWTRequest). For a
 * normal owner viewing their own bot the param is a harmless no-op (the backend
 * ignores `customer_id` for non-privileged callers).
 *
 * Pass `botId=''` on workspace-level pages where no bot scoping applies. If a
 * customer id is available (admin/reseller impersonation), pass it as the second
 * argument so the same owner-scoped list is cached for the sidebar.
 */
import { useQuery } from '@tanstack/react-query';
import { useApiHeaders } from '../../hooks/useApiHeaders';
import { useAuthnMe } from '../../AuthnMeProvider';

export interface NavBot { bot_id: string; bot_name: string }

export interface NavBotsResult {
  bots: NavBot[];
  customerId?: string;
  customerName?: string;
  loading?: boolean;
}

const EMPTY_NAV_BOTS: NavBotsResult = { bots: [] };

export function useNavBots(botId: string, customerId?: string): NavBotsResult {
  const authn = useAuthnMe();
  const apiHeaders = useApiHeaders('');

  const query = useQuery<NavBotsResult>({
    queryKey: ['nav-bots', botId ? `bot:${botId}` : `customer:${customerId || 'self'}`],
    enabled: !!authn?.isAuthenticated,
    staleTime: 30_000,
    queryFn: async () => {
      // 1) Resolve the bot's owning customer from its config so the nav is
      //    scoped to that customer (not the impersonating operator).
      let q = '';
      if (botId) {
        try {
          const r = await fetch(`/api/bot/${botId}/config`, { headers: apiHeaders() });
          if (r.ok) {
            const cfg = await r.json();
            if (cfg && cfg.customer_id) q = `?customer_id=${encodeURIComponent(cfg.customer_id)}`;
          }
        } catch { /* fall back to caller-scoped list below */ }
      } else if (customerId) {
        q = `?customer_id=${encodeURIComponent(customerId)}`;
      }
      // 2) Fetch the (owner-scoped) bot list for the nav.
      try {
        const r = await fetch(`/api/customer/bots${q}`, { headers: apiHeaders() });
        if (!r.ok) return { bots: [] };
        const d = await r.json();
        return {
          bots: (d.bots as NavBot[]) || [],
          customerId: d.customer_id,
          customerName: d.customer_name,
        };
      } catch {
        // Preserve the old hook behavior: fetch failures leave nav empty.
        return { bots: [] };
      }
    },
  });

  return query.data
    ? { ...query.data, loading: query.isLoading }
    : { ...EMPTY_NAV_BOTS, loading: query.isLoading };
}
